import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Table, Tooltip } from 'antd'
import { useMeasure, useDebounce } from 'react-use'

const subsystemsTableColumns = [
  {
    title: <FormattedMessage id="functionalDiagrams.subsystemsTable.system" />,
    dataIndex: ['system', 'name'],
    key: 'system',
    width: '50%',
    ellipsis: true,
    render: (name, obj) => {
      const msg = obj.system.translated_description || name
      return (
        <Tooltip placement="topLeft" title={msg}>
          {msg}
        </Tooltip>
      )
    },
  },
  {
    title: (
      <FormattedMessage id="functionalDiagrams.subsystemsTable.subsystem" />
    ),
    dataIndex: 'name',
    key: 'subsystem',
    width: '50%',
    ellipsis: true,
    render: (name, obj) => {
      const msg = obj.translated_description || name
      return (
        <Tooltip placement="topLeft" title={msg}>
          {msg}
        </Tooltip>
      )
    },
  },
]

export function SubsystemsViewTable({
  subsystems,
  searchString,
  systemTypesFilter,
  pagination,
  susbsystemCallback,
  // Rendered inside the table's own frame, above the column headers, for callers
  // that show several of these tables at once and have to say what each one is.
  title,
  emptyText,
}) {
  const [selectedRowKey, setSelectedRowKey] = useState(null)
  const [containerRef, { height: containerHeight }] = useMeasure()
  const [tableHeight, setTableHeight] = React.useState(0)

  // containerHeight changes its height multiple times during re-render
  // but only the largest (last) value is needed.
  useDebounce(
    () => {
      setTableHeight(containerHeight - 50) // 50 is the height of header.
    },
    1,
    [containerHeight],
  )

  const defaultPagination = { pageSize: 15, hideOnSinglePage: true }
  const mergedPagination =
    pagination === false
      ? false
      : { ...defaultPagination, ...(pagination || {}) }

  const filteredSubsystems = searchList({
    items: subsystems?.map((item) => {
      return {
        ...item,
        key: item.id,
      }
    }),
    searchString,
    fields: [['name'], ['system', 'name']],
    includeFilter: systemTypesFilter,
    includeField: ['system', 'name'],
  })

  const selectSubsystem = ({ record }) => {
    setSelectedRowKey(record.id)
    susbsystemCallback({ record })
  }

  return (
    <div
      className="subsystems-table"
      ref={containerRef}
      style={{ height: '100%' }}
    >
      <Table
        rowSelection={{
          type: 'radio',
          onSelect: (record, selected, selectedRows, nativeEvent) =>
            selectSubsystem({ record }),
          selectedRowKeys: [selectedRowKey],
        }}
        size="middle"
        title={title ? () => title : undefined}
        locale={emptyText ? { emptyText } : undefined}
        dataSource={filteredSubsystems}
        columns={subsystemsTableColumns}
        pagination={mergedPagination}
        scroll={
          mergedPagination
            ? false
            : {
                y: tableHeight,
              }
        }
        onRow={(record, rowIndex) => ({
          onClick: (event) => selectSubsystem({ record }),
        })}
        data-testid="subsystems-table"
      />
    </div>
  )
}

export function searchList({
  items = [],
  searchString = '',
  fields = [],
  includeFilter = [],
  includeField = [],
}) {
  if (searchString) {
    searchString = searchString.toLowerCase()

    items = items.filter((object) =>
      fields.some((field) =>
        getValueByProps({ object, props: field })
          .toLowerCase()
          .includes(searchString),
      ),
    )
  }

  if (includeField && includeFilter && includeFilter.length) {
    const types = new Set(includeFilter)
    items = items.filter((object) =>
      types.has(getValueByProps({ object, props: includeField })),
    )
  }

  return items
}

const getValueByProps = ({ object, props }) => {
  return props.reduce((acc, prop) => acc[prop], object)
}

export default SubsystemsViewTable
